import { Carousel } from "antd";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./style.module.scss";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";

export interface VLCarouselProps {
  contentList: ReactElement[];
  afterChange?: (index: number) => void;
  totalText?: string;
  selectedItem?: number;
  totalCount?: number;
}

const MINIMUM_SLIDES_TO_SHOW = 6;

export default function VLCarousel({
  contentList,
  afterChange,
  totalText,
  selectedItem = 0,
  totalCount,
}: VLCarouselProps) {
  const carouselRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(selectedItem);

  const slidesToShow =
    contentList.length > MINIMUM_SLIDES_TO_SHOW
      ? MINIMUM_SLIDES_TO_SHOW
      : contentList.length;

  const totalItems = totalCount || contentList.length;

  // Clamp selectedItem to available content for carousel navigation
  const clampedSelectedItem = Math.min(selectedItem, contentList.length - 1);

  useEffect(() => {
    wrapperRef.current?.focus();
    if (carouselRef.current && clampedSelectedItem !== currentSlide) {
      carouselRef.current.goTo(clampedSelectedItem, false);
      setCurrentSlide(selectedItem); // Keep original selectedItem for counter display
    }
  }, [selectedItem, currentSlide, clampedSelectedItem]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.buttons}>
        <div className={styles.totalText}>
          {totalText
            ? `${totalText} ${totalItems}`
            : `Additional Items: ${totalItems}`}
        </div>
        <div className={styles.centerButtons}>
          <div
            className={styles.button}
            onClick={() => carouselRef.current?.prev()}
          >
            <ArrowLeftOutlined />
          </div>
          <div>{`${currentSlide + 1} / ${totalItems}`}</div>
          <div
            className={styles.button}
            onClick={() => carouselRef.current?.next()}
          >
            <ArrowRightOutlined />
          </div>
        </div>
      </div>

      <div
        ref={wrapperRef}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") carouselRef.current?.prev();
          if (e.key === "ArrowRight") carouselRef.current?.next();
        }}
      >
        <Carousel
          ref={carouselRef}
          initialSlide={clampedSelectedItem}
          dots={false}
          arrows={false}
          slidesToShow={slidesToShow}
          lazyLoad={"ondemand"}
          slidesToScroll={1}
          centerMode={true}
          draggable={true}
          afterChange={(index) => {
            setCurrentSlide(index);
            afterChange?.(index);
          }}
        >
          {contentList.map((content, index) => (
            <div
              key={index}
              className={classNames(styles.content)}
              onClick={() => carouselRef.current?.goTo(index)}
            >
              {content}
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
}
